import { Col, Container, Row } from 'reactstrap';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useControllerDashboardAdmin from './DashboardAdmin/_presenters/useControllerDashboardAdmin/useControllerDashboardAdmin';
import {
  StatCard,
  QuickActionsCard,
  RecentItemsCard,
  LicenseStatusCard
} from 'components/Dashboard';
import { ROUTES_PATHS } from 'navigations/routes';

const DashboardPageAdm = () => {
  const navigate = useNavigate();
  const {
    stats,
    isLoading,
    recentCustomers,
    recentLicenses,
    recentNotifications
  } = useControllerDashboardAdmin();

  const routes = ROUTES_PATHS.LOGGED_PAGES.ADMIN;

  const quickActions = useMemo(
    () => [
      {
        title: 'Novo Cliente',
        description: 'Cadastrar um novo cliente',
        icon: 'building',
        to: routes.CUSTOMERS.CREATE,
        iconColor: 'text-success'
      },
      {
        title: 'Nova Licença',
        description: 'Criar uma nova licença',
        icon: 'key',
        to: routes.LICENCES.CREATE,
        iconColor: 'text-primary'
      },
      {
        title: 'Nova Notificação',
        description: 'Enviar notificação aos clientes',
        icon: 'bell',
        to: routes.NOTIFICATIONS.CREATE,
        iconColor: 'text-warning'
      },
      {
        title: 'Novo Canal de Notícias',
        description: 'Adicionar canal de notícias',
        icon: 'rss',
        to: routes.NEWS_CHANNELS.CREATE,
        iconColor: 'text-info'
      }
    ],
    [routes]
  );

  const recentCustomersFormatted = useMemo(
    () =>
      recentCustomers.map((c) => ({
        id: c.id || '',
        name: c.name || 'Sem nome',
        extra: c.email
      })),
    [recentCustomers]
  );

  const recentLicensesFormatted = useMemo(
    () =>
      recentLicenses.map((l) => ({
        id: l.id || '',
        name: l.customer?.name || 'Sem cliente',
        status: l.active,
        extra: l.inUse ? 'Em uso' : 'Disponível'
      })),
    [recentLicenses]
  );

  const recentNotificationsFormatted = useMemo(
    () =>
      recentNotifications.map((n) => ({
        id: n.id || '',
        name: n.title || 'Sem título',
        extra: new Date(n.createdAt).toLocaleDateString('pt-BR')
      })),
    [recentNotifications]
  );

  return (
    <>
      <div className="py-4">
        <Container fluid>
          {/* Header */}
          <Row className="mb-4">
            <Col>
              <h2 className="fw-bold mb-1">Dashboard Administrativo</h2>
              <p className="text-muted mb-0">
                Visão geral da plataforma Index Player
              </p>
            </Col>
          </Row>

          {/* Stats Cards */}
          <Row className="g-4 mb-4">
            <Col xs={12} sm={6} lg={3}>
              <StatCard
                title="Clientes"
                value={stats.customers.total}
                icon="building"
                iconBgColor="bg-primary"
                isLoading={isLoading}
                onClick={() => navigate(routes.CUSTOMERS.LIST)}
              />
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <StatCard
                title="Licenças"
                value={stats.licenses.total}
                subtitle={`${stats.licenses.available} disponíveis`}
                icon="key"
                iconBgColor="bg-success"
                isLoading={isLoading}
                onClick={() => navigate(routes.LICENCES.LIST)}
              />
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <StatCard
                title="Usuários Admin"
                value={stats.adminUsers.total}
                icon="people"
                iconBgColor="bg-warning"
                isLoading={isLoading}
                onClick={() => navigate(routes.USERS.LIST)}
              />
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <StatCard
                title="Notificações"
                value={stats.notifications.total}
                icon="bell"
                iconBgColor="bg-info"
                isLoading={isLoading}
                onClick={() => navigate(routes.NOTIFICATIONS.LIST)}
              />
            </Col>
          </Row>

          {/* Secondary Stats */}
          <Row className="g-4 mb-4">
            <Col xs={12} sm={6} lg={4}>
              <StatCard
                title="Canais de Notícias"
                value={stats.newsChannels.total}
                icon="rss"
                iconBgColor="bg-secondary"
                isLoading={isLoading}
                onClick={() => navigate(routes.NEWS_CHANNELS.LIST)}
              />
            </Col>
            <Col xs={12} sm={6} lg={4}>
              <StatCard
                title="Categorias"
                value={stats.newsCategories.total}
                icon="tags"
                iconBgColor="bg-dark"
                isLoading={isLoading}
                onClick={() => navigate(routes.NEWS_CATEGORIES.LIST)}
              />
            </Col>
            <Col xs={12} sm={6} lg={4}>
              <StatCard
                title="Contatos de Suporte"
                value={stats.supportContacts.total}
                icon="headset"
                iconBgColor="bg-danger"
                isLoading={isLoading}
                onClick={() => navigate(routes.SUPPORT_CONTACTS.LIST)}
              />
            </Col>
          </Row>

          {/* Main Content */}
          <Row className="g-4 mb-4">
            {/* License Status */}
            <Col xs={12} lg={6}>
              <LicenseStatusCard
                total={stats.licenses.total}
                active={stats.licenses.active}
                inUse={stats.licenses.inUse}
                available={stats.licenses.available}
                isLoading={isLoading}
              />
            </Col>

            {/* Quick Actions */}
            <Col xs={12} lg={6}>
              <QuickActionsCard actions={quickActions} />
            </Col>
          </Row>

          {/* Recent Items */}
          <Row className="g-4">
            <Col xs={12} lg={4}>
              <RecentItemsCard
                title="Clientes Recentes"
                icon="building"
                items={recentCustomersFormatted}
                viewAllLink={routes.CUSTOMERS.LIST}
                isLoading={isLoading}
                emptyMessage="Nenhum cliente cadastrado"
              />
            </Col>
            <Col xs={12} lg={4}>
              <RecentItemsCard
                title="Licenças Recentes"
                icon="key"
                items={recentLicensesFormatted}
                viewAllLink={routes.LICENCES.LIST}
                isLoading={isLoading}
                emptyMessage="Nenhuma licença criada"
                statusLabels={{ active: 'Ativa', inactive: 'Inativa' }}
              />
            </Col>
            <Col xs={12} lg={4}>
              <RecentItemsCard
                title="Notificações Recentes"
                icon="bell"
                items={recentNotificationsFormatted}
                viewAllLink={routes.NOTIFICATIONS.LIST}
                isLoading={isLoading}
                emptyMessage="Nenhuma notificação enviada"
              />
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default DashboardPageAdm;
